<template>
  <div class="result flex-column">
    <modal-header-with-previous
      class="result__modal-header"
      current-modal-name="photo-analysis-result-update-modal"
      previous-modal-name="analysis-results-overview-modal"
      is-custom-close-handler
      @close-modal="beforeCloseModal"
    >
      <span slot="header-title" class="header__title">
        {{ $t('dashboard.photoAnalysis.label.stepOne') }}
      </span>
    </modal-header-with-previous>
    <main class="result__content">
      <section class="result__questions flex-column">
        <zoomable-photo v-if="photoUrl" class="zoomable-photo-mobile" :photo="photoUrl" />
        <div
          ref="age-group-container"
          class="field-container personal-data-select"
          style="width:330px;"
        >
          <div class="field-container__title">
            {{ $t('dashboard.photoAnalysis.age') }}
            <i>({{ $t('dashboard.photoAnalysis.label.specify') }})</i>
          </div>
          <q-select
            group-label="ageGroup"
            group-values="ages"
            :group-select="false"
            :options="ageSelectOptions"
            :value="getNestedProperty(selectFieldsData, 'characteristics.ageGroup')"
            @input="onAgeGroupFieldChange('ageGroup', $event)"
          />
        </div>
        <div
          v-for="field in personalDataFields"
          :key="field.label"
          class="field-container personal-data-select"
          style="width:330px;"
        >
          <div class="field-container__title">
            {{ field.label }}
          </div>
          <span v-if="field.tooltip" v-tooltip="field.tooltip" class="custom-icon icon-info"></span>
          <q-select
            label="label"
            :allow-empty="false"
            :placeholder="$t('dashboard.diagnosticDetails.selectOption')"
            :value="
              getNestedProperty(
                selectFieldsData,
                'characteristics'.concat('.'.concat(field.propertyName))
              )
            "
            :options="field.options"
            track-by="label"
            @input="onCaractheristicFieldChange(field.propertyName, $event)"
          />
        </div>
        <zoomable-photo
          v-if="photoUrl"
          class="zoomable-photo-desktop"
          :photo="photoUrl"
          type="big"
        />
      </section>
      <section ref="scores-section" class="result__scores">
        <div
          v-for="section in personalizedSectionsBinding"
          :key="section.name"
          class="result__section"
        >
          <slide-down :show="isOpened" class="diagnostic-dropdown">
            <div slot="toggle">
              <div class="flex--space-between--center pointer">
                <div style="width:60px">{{ section.name }}</div>
                <div>
                  <diagnostic-dysfunction-slider
                    :value="sectionsScores[section.propertyName]"
                    :color="section.color"
                    @change="updateScore(section.propertyName, $event)"
                  />
                </div>
                <span
                  class="arrow-icon fas fa-chevron-down"
                  :class="{ 'arrow-icon--opened': isOpened }"
                  style="background-color: #f3f3f8; padding: 4px; border-radius: 3px; margin: 4px;"
                ></span>
              </div>
            </div>
            <div
              slot="slide-body"
              class="treatment__content"
              style="background-color: #ffffff"
              :class="{ 'treatment__content--opened': isOpened }"
            >
              <section class="section__questions" style="align-items: flex-start">
                <div
                  v-for="question in section.questions"
                  :key="question.label"
                  class="field-container question__field"
                >
                  <div class="question__label">
                    {{ question.label }}
                    <v-select
                      label="label"
                      :allow-empty="false"
                      :placeholder="$t('dashboard.medicalData.selectOption')"
                      :value="
                        getNestedProperty(
                          selectFieldsData,
                          section.propertyName.concat(
                            '.'.concat(question.propertyName.concat('.description'))
                          )
                        )
                      "
                      :options="question.options"
                      track-by="label"
                      class="style-chooser"
                      @input="onFieldChange(section.propertyName, question.propertyName, $event)"
                    />
                  </div>
                </div>
              </section>
            </div>
          </slide-down>
        </div>
      </section>
    </main>

    <dashboard-constraint
      id="skinAnalysisConstraint"
      v-model="skinAnalysisConstraint"
      name="skinAnalysisConstraint"
      required
      class="skin-analysis"
    >
      <template slot="constraint">
        <span
          id="dashboard-consent"
          class="skin-analysis__constraint"
          :class="{ error: hasSkinAnalysisConstraintError }"
        >
          {{ $t('dashboard.consent.diagnostic.providerConfirmation') }}
        </span>
      </template>
    </dashboard-constraint>

    <mobile-fixed-footer class="result__footer flex--center--center">
      <button
        slot="footer"
        class="rounded-button-white"
        :class="{ 'rounded-button-white__alma': isAlmaTerritory }"
        @click="onNextClick"
      >
        {{ $t('action.next') }}
      </button>
    </mobile-fixed-footer>

    <quit-photo-analysis-warning-modal class="onlyXS" return-to-opened-patient />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import PerfectScrollbar from 'perfect-scrollbar';
import { get, isNumber, set } from 'lodash';
import ModalHeaderWithPrevious from '@/modules/dashboard/components/dashboard/common/ModalHeaderWithPrevious';
import QSelect from '@/modules/dashboard/components/common/Select';
import VSelect from '@/modules/dashboard/components/common/VSelect';
import SlideDown from '@/modules/dashboard/components/common/SlideDown';
import MobileFixedFooter from '@/modules/dashboard/components/dashboard/common/MobileFixedFooter';
import ZoomablePhoto from '@/modules/dashboard/components/dashboard/create-treatment/photo-analysis/ZoomablePhoto';
import QuitPhotoAnalysisWarningModal from '@/modules/dashboard/components/dashboard/create-treatment/photo-analysis/QuitPhotoAnalysisWarningModal';

import SelectDataBinding from '@/components/common/SelectDataBinding';
import PhotoAnalysisResultSection from '@/components/common/PhotoAnalysisResultSection';
import RoutingInfo from '@/modules/dashboard/components/common/RoutingInfo';

import { fetchPatientPhotoByPhotoId } from '@/modules/dashboard/api';
import DiagnosticDysfunctionSlider from '@/modules/dashboard/components/common/DiagnosticDysfunctionSlider';
import DashboardConstraint from '@/modules/dashboard/components/dashboard/common/DashboardConstraint';
import isAlmaTerritories from '@/modules/dashboard/components/mixins/isAlmaTerritories';

import {
  buildImageUrl,
  getMedicalDataSelectOptionsWithLabel,
  getSelectOptionsWithLabel,
  handleRoutingInfo,
  isMobileDevice
} from '@/modules/dashboard/utils';

import { GENDER_VALUES } from '@/modules/questionnaire/api/constants';

import {
  ACTIVES,
  DYSFUNCTIONS_COLORS,
  // PERSON_DATA_FIELDS_DEFAULT_VALUES,
  // SECTION_DATA_FIELDS_DEFAULT_VALUES,
  PERSON_DATA_FIELDS_NAMES,
  SELECT_DATA_FIELDS_NAMES,
  ANALYSIS_RESULT_COLORS_ARRAY,
  BASE_DESCRIPTOR,
  ANALYSIS_RESULT_COLORS
} from '@/modules/dashboard/api/constants';

import {
  PHOTOTYPE,
  SKIN_THICKNESS,
  YES_NO,
  NO_MODERATE_SEVERE,
  NO_MILD_MODERATE_SEVERE
} from '@/modules/dashboard/api/medicalData';

import { types } from '@/modules/dashboard/store/types';

const PERSON_DATA_FIELDS_DEFAULT_VALUES = [
  'ethnicity',
  'eyeColor',
  'hairColor',
  'skinType',
  'estimatedAge'
];

const SELECT_OPTIONS_FIELDS = {
  genderSelectOptions: null,
  ageGroupOptions: null,
  phototypeSelectOptions: null,
  skinThinknessSelectOptions: null,
  yesNoSelectOptions: null,
  noModerateSevereSelectOptions: null,
  noMildModerateSevereSelectOptions: null,
  yComplexionSelectOptions: null,
  yTirednessSelectOptions: null,
  ySunDamageOptions: null,
  pWrinklesOptions: null,
  pFineLineseOptions: null,
  pElasticitySaggingOptions: null,
  pVolumeeOptions: null,
  rRednessPresentOptions: null,
  rCouperosePresentOptions: null,
  rIsRosaceaOptions: null,
  rIsSunburnOptions: null,
  rIsContactDermatitisOptions: null,
  rIsEczemaOptions: null,
  rIsPsoriasisOptions: null,
  rIsInfectionsOptions: null,
  rIsAcneOptions: null,
  rIsAllergicReactionOptions: null,
  bObservedDrynessOptions: null,
  bObservedDehydrationOptions: null,
  bPredictiveFactorsDrynessOptions: null,
  bPredictiveFactorsDehydrationOptions: null,
  oOilinessOptions: null,
  oPoresOptions: null,
  greyRoughBumpySkinOptions: null,
  greyDullSkinOptions: null,
  greyUnevenSkinTextureOptions: null,
  greyRoughnessOptions: null,
  greyScarringOptions: null,
  greenComedonesOptions: null,
  greenPustulesOptions: null,
  greenPapulesOptions: null,
  greenNodulesOptions: null,
  greenCystsOptions: null,
  brownMelasmaOptions: null,
  brownPostInflammatoryHyperpigmentationOptions: null,
  brownAgeSunSpotsOptions: null,
  brownFrecklesOptions: null,
  brownMolesOptions: null,
  brownSkinToneOptions: null,
  brownPredictiveFactorsHyperpigmentationOptions: null
};

const PHOTO_ANALYSIS_SELECT_OPTIONS_BINDING = [
  { propertyName: 'genderSelectOptions', options: GENDER_VALUES, keyPrefix: 'dashboard.gender.' },
  {
    propertyName: 'skinThinknessSelectOptions',
    options: SKIN_THICKNESS,
    keyPrefix: 'dashboard.photoAnalysis.'
  }
];

const OPT_COMPLEXION = ['complexion1', 'complexion2', 'complexion3', 'complexion4'];
const OPT_TIREDNESS = ['tiredness1', 'tiredness2', 'tiredness3', 'tiredness4'];
const OPT_SUNDAMAGE = ['sun_damage1', 'sun_damage2', 'sun_damage3', 'sun_damage4'];
const OPT_WRINKLESE = ['wrinkles1', 'wrinkles2', 'wrinkles3', 'wrinkles4'];
const OPT_FINELINESE = ['fine_lines1', 'fine_lines2', 'fine_lines3', 'fine_lines4'];
const OPT_ELASTICITYSAGGING = ['sagging1', 'sagging2', 'sagging3', 'sagging4'];
const OPT_VOLUME = ['volume1', 'volume2', 'volume3', 'volume4'];
const OPT_REDNESSPRESENT = ['redness1', 'redness2', 'redness3', 'redness4', 'redness5'];
const OPT_COUPEROSEPRESENT = ['couperose1', 'couperose2', 'couperose3', 'couperose4', 'couperose5'];
const OPT_ISROSACEA = ['rosacea1', 'rosacea2', 'rosacea3'];
const OPT_ISSUNBURN = ['sunburn1', 'sunburn2', 'sunburn3'];
const OPT_ISCONTACTDERMATITIS = [
  'contact_dermatitis1',
  'contact_dermatitis2',
  'contact_dermatitis3'
];
const OPT_ISECZEMA = ['eczema1', 'eczema2', 'eczema3'];
const OPT_ISPSORIASIS = ['psoriasis1', 'psoriasis2', 'psoriasis3'];
const OPT_ISINFECTIONS = ['infections1', 'infections2', 'infections3'];
const OPT_ISACNE = ['acne1', 'acne2', 'acne3'];
const OPT_ISALLERGICREACTION = ['allergic_reaction1', 'allergic_reaction2', 'allergic_reaction3'];

const OPT_OBSERVEDDRYNESS = ['dryness1', 'dryness2', 'dryness3', 'dryness4'];
const OPT_OBSERVEDDEHYDRATION = ['dehydration1', 'dehydration2', 'dehydration3', 'dehydration4'];
const OPT_PREDICTIVEFACTORSDRYNESS = [
  'predictive_factors_dryness1',
  'predictive_factors_dryness2',
  'predictive_factors_dryness3',
  'predictive_factors_dryness4'
];
const OPT_PREDICTIVEFACTORSDEHYDRATION = [
  'predictive_factors_dehydration1',
  'predictive_factors_dehydration2',
  'predictive_factors_dehydration3',
  'predictive_factors_dehydration4'
];
const OPT_OILINESS = ['oiliness1', 'oiliness2', 'oiliness3', 'oiliness4'];
const OPT_PORES = ['pores1', 'pores2', 'pores3', 'pores4'];
const OPT_ROUGHBUMPYSKIN = [
  'rough_bumpy_skin1',
  'rough_bumpy_skin2',
  'rough_bumpy_skin3',
  'rough_bumpy_skin4'
];
const OPT_DULLSKIN = ['dull_skin1', 'dull_skin2', 'dull_skin3', 'dull_skin4'];
const OPT_UNEVENSKINTEXTURE = [
  'uneven_skin_texture1',
  'uneven_skin_texture2',
  'uneven_skin_texture3',
  'uneven_skin_texture4'
];
const OPT_ROUGHNESS = ['roughness1', 'roughness2', 'roughness3', 'roughness4'];

const OPT_SCARRING = ['scarring1', 'scarring2', 'scarring3'];
const OPT_COMEDONES = ['comedones1', 'comedones2', 'comedones3', 'comedones4'];
const OPT_PUSTULES = ['pustules1', 'pustules2', 'pustules3', 'pustules4'];
const OPT_PAPULES = ['papules1', 'papules2', 'papules3', 'papules4'];
const OPT_NODULES = ['nodules1', 'nodules2', 'nodules3', 'nodules4'];
const OPT_CYSTS = ['cysts1', 'cysts2', 'cysts3', 'cysts4'];
const OPT_MELASMA = ['melasma1', 'melasma2', 'melasma3', 'melasma4'];
const OPT_POSTINFLAMMATORYHYPERPIGMENTATION = ['pih1', 'pih2', 'pih3', 'pih4'];
const OPT_AGESUNSPOTS = ['age_sun_spots1', 'age_sun_spots2', 'age_sun_spots3', 'age_sun_spots4'];

const OPT_FRECKLES = ['freckles1', 'freckles2', 'freckles3'];
const OPT_MOLES = ['moles1', 'moles2', 'moles3'];
const OPT_SKINTONE = ['skin_tone1', 'skin_tone2', 'skin_tone3', 'skin_tone4'];
const OPT_PREDICTIVEFACTORSHYPERPIGMENTATION = [
  'predictive_factors_hyperpigmentation1',
  'predictive_factors_hyperpigmentation2'
];

const MEDICAL_DATA_SELECT_OPTIONS_BINDING = [
  { propertyName: 'phototypeSelectOptions', options: PHOTOTYPE },
  { propertyName: 'yesNoSelectOptions', options: YES_NO },
  { propertyName: 'noModerateSevereSelectOptions', options: NO_MODERATE_SEVERE },
  { propertyName: 'noMildModerateSevereSelectOptions', options: NO_MILD_MODERATE_SEVERE },
  { propertyName: 'yComplexionSelectOptions', options: OPT_COMPLEXION },
  { propertyName: 'yTirednessSelectOptions', options: OPT_TIREDNESS },
  { propertyName: 'ySunDamageOptions', options: OPT_SUNDAMAGE },
  { propertyName: 'pWrinklesOptions', options: OPT_WRINKLESE },
  { propertyName: 'pFineLineseOptions', options: OPT_FINELINESE },
  { propertyName: 'pElasticitySaggingOptions', options: OPT_ELASTICITYSAGGING },
  { propertyName: 'pVolumeeOptions', options: OPT_VOLUME },
  { propertyName: 'rRednessPresentOptions', options: OPT_REDNESSPRESENT },
  { propertyName: 'rCouperosePresentOptions', options: OPT_COUPEROSEPRESENT },
  { propertyName: 'rIsRosaceaOptions', options: OPT_ISROSACEA },
  { propertyName: 'rIsSunburnOptions', options: OPT_ISSUNBURN },
  { propertyName: 'rIsContactDermatitisOptions', options: OPT_ISCONTACTDERMATITIS },
  { propertyName: 'rIsEczemaOptions', options: OPT_ISECZEMA },
  { propertyName: 'rIsPsoriasisOptions', options: OPT_ISPSORIASIS },
  { propertyName: 'rIsInfectionsOptions', options: OPT_ISINFECTIONS },
  { propertyName: 'rIsAcneOptions', options: OPT_ISACNE },
  { propertyName: 'rIsAllergicReactionOptions', options: OPT_ISALLERGICREACTION },
  { propertyName: 'bObservedDrynessOptions', options: OPT_OBSERVEDDRYNESS },
  { propertyName: 'bObservedDehydrationOptions', options: OPT_OBSERVEDDEHYDRATION },
  { propertyName: 'bPredictiveFactorsDrynessOptions', options: OPT_PREDICTIVEFACTORSDRYNESS },
  {
    propertyName: 'bPredictiveFactorsDehydrationOptions',
    options: OPT_PREDICTIVEFACTORSDEHYDRATION
  },
  { propertyName: 'oOilinessOptions', options: OPT_OILINESS },
  { propertyName: 'oPoresOptions', options: OPT_PORES },
  { propertyName: 'greyRoughBumpySkinOptions', options: OPT_ROUGHBUMPYSKIN },
  { propertyName: 'greyDullSkinOptions', options: OPT_DULLSKIN },
  { propertyName: 'greyUnevenSkinTextureOptions', options: OPT_UNEVENSKINTEXTURE },
  { propertyName: 'greyRoughnessOptions', options: OPT_ROUGHNESS },
  { propertyName: 'greyScarringOptions', options: OPT_SCARRING },
  { propertyName: 'greenComedonesOptions', options: OPT_COMEDONES },
  { propertyName: 'greenPustulesOptions', options: OPT_PUSTULES },
  { propertyName: 'greenPapulesOptions', options: OPT_PAPULES },
  { propertyName: 'greenNodulesOptions', options: OPT_NODULES },
  { propertyName: 'greenCystsOptions', options: OPT_CYSTS },
  { propertyName: 'brownMelasmaOptions', options: OPT_MELASMA },
  {
    propertyName: 'brownPostInflammatoryHyperpigmentationOptions',
    options: OPT_POSTINFLAMMATORYHYPERPIGMENTATION
  },
  { propertyName: 'brownAgeSunSpotsOptions', options: OPT_AGESUNSPOTS },
  { propertyName: 'brownFrecklesOptions', options: OPT_FRECKLES },
  { propertyName: 'brownMolesOptions', options: OPT_MOLES },
  { propertyName: 'brownSkinToneOptions', options: OPT_SKINTONE },
  {
    propertyName: 'brownPredictiveFactorsHyperpigmentationOptions',
    options: OPT_PREDICTIVEFACTORSHYPERPIGMENTATION
  }
];

const SECTIONS_SCORE_DEFAULT_VALUES = {
  yellow: 0,
  pink: 0,
  red: 0,
  blue: 0,
  orange: 0,
  grey: 0,
  green: 0,
  brown: 0
};

const SECTIONS_SCORES_FIELDS = Object.keys(SECTIONS_SCORE_DEFAULT_VALUES);

const CLARIFICATION_ROUNTING_INFO = new RoutingInfo(
  'clarification-modal',
  'PhotoAnalysisClarification'
);

const FIELD_TO_OPTIONS_PROPERTY_BINDING = {
  gender: 'genderSelectOptions',
  phototype: 'phototypeSelectOptions',
  skinThickness: 'skinThinknessSelectOptions',
  hasAcneScars: 'yesNoSelectOptions',
  wrinklesDepth: 'noModerateSevereSelectOptions',
  hasRosacea1: 'yesNoSelectOptions',
  hasCouperose: 'yesNoSelectOptions',
  dryness: 'noMildModerateSevereSelectOptions',
  dehydration: 'noMildModerateSevereSelectOptions',
  hasAcneVulgaris: 'yesNoSelectOptions',
  hasAcneRosacea: 'yesNoSelectOptions',
  hasAcneNodular: 'yesNoSelectOptions',
  comesFromPIH: 'yesNoSelectOptions',
  comesFromAgeSpotsMelasma: 'yesNoSelectOptions',
  sensitiveSkin: 'yesNoSelectOptions',
  complexion: 'yComplexionSelectOptions',
  tiredness: 'yTirednessSelectOptions',
  sunDamage: 'ySunDamageOptions',
  wrinkles: 'pWrinklesOptions',
  fineLines: 'pFineLineseOptions',
  elasticitySagging: 'pElasticitySaggingOptions',
  volume: 'pVolumeeOptions',
  rednessPresent: 'rRednessPresentOptions',
  couperosePresent: 'rCouperosePresentOptions',
  isRosacea: 'rIsRosaceaOptions',
  isSunburn: 'rIsSunburnOptions',
  isContactDermatitis: 'rIsContactDermatitisOptions',
  isEczema: 'rIsEczemaOptions',
  isPsoriasis: 'rIsPsoriasisOptions',
  isInfections: 'rIsInfectionsOptions',
  isAcne: 'rIsAcneOptions',
  isAllergicReaction: 'rIsAllergicReactionOptions',
  observedDryness: 'bObservedDrynessOptions',
  observedDehydration: 'bObservedDehydrationOptions',
  predictiveFactorsDryness: 'bPredictiveFactorsDrynessOptions',
  predictiveFactorsDehydration: 'bPredictiveFactorsDehydrationOptions',
  oiliness: 'oOilinessOptions',
  pores: 'oPoresOptions',
  roughBumpySkin: 'greyRoughBumpySkinOptions',
  dullSkin: 'greyDullSkinOptions',
  unevenSkinTexture: 'greyUnevenSkinTextureOptions',
  roughness: 'greyRoughnessOptions',
  scarring: 'greyScarringOptions',
  comedones: 'greenComedonesOptions',
  pustules: 'greenPustulesOptions',
  papules: 'greenPapulesOptions',
  nodules: 'greenNodulesOptions',
  cysts: 'greenCystsOptions',
  melasma: 'brownMelasmaOptions',
  postInflammatoryHyperpigmentation: 'brownPostInflammatoryHyperpigmentationOptions',
  ageSunSpots: 'brownAgeSunSpotsOptions',
  freckles: 'brownFrecklesOptions',
  moles: 'brownMolesOptions',
  skinTone: 'brownSkinToneOptions',
  predictiveFactorsHyperpigmentation: 'brownPredictiveFactorsHyperpigmentationOptions'
};

export const AGE_GROUPS = [
  {
    ageGroup: 'young',
    // Ages from 12 to 19.
    ages: [...Array.from(Array(8)).keys()].map(key => key + 12)
  },
  {
    ageGroup: 'youngAdult',
    // Ages from 20 to 29.
    ages: [...Array.from(Array(10)).keys()].map(key => key + 20)
  },
  {
    ageGroup: 'adult',
    // Ages from 30 to 49.
    ages: [...Array.from(Array(20)).keys()].map(key => key + 30)
  },
  {
    ageGroup: 'senior',
    // Ages from 50 to 120.
    ages: [...Array.from(Array(71)).keys()].map(key => key + 50)
  }
];

const TOOLTIP_CONFIG = {
  sensitiveSkin: {
    content: 'dashboard.dysfunction.sensitiveSkin.hint'
  }
};

const CHARS_CONFIG = {
  characteristics: {
    gender: '',
    ageGroup: '',
    eyeColor: '',
    skinType: '',
    ethnicity: '',
    hairColor: '',
    phototype: '',
    estimatedAge: 0,
    sensitiveSkin: '',
    skinThickness: ''
  }
};

const SECTIONS_DYSFUNCTIONS_FIELDS_V2 = {
  [ANALYSIS_RESULT_COLORS.YELLOW]: {
    dysfunctionScore: 0,
    complexion: { ...BASE_DESCRIPTOR },
    tiredness: { ...BASE_DESCRIPTOR },
    sunDamage: { ...BASE_DESCRIPTOR }
  },
  [ANALYSIS_RESULT_COLORS.PINK]: {
    dysfunctionScore: 0,
    wrinkles: { ...BASE_DESCRIPTOR },
    fineLines: { ...BASE_DESCRIPTOR },
    elasticitySagging: { ...BASE_DESCRIPTOR },
    volume: { ...BASE_DESCRIPTOR }
  },
  [ANALYSIS_RESULT_COLORS.RED]: {
    dysfunctionScore: 0,
    rednessPresent: { ...BASE_DESCRIPTOR },
    couperosePresent: { ...BASE_DESCRIPTOR },
    isRosacea: { ...BASE_DESCRIPTOR },
    isSunburn: { ...BASE_DESCRIPTOR },
    isContactDermatitis: { ...BASE_DESCRIPTOR },
    isEczema: { ...BASE_DESCRIPTOR },
    isPsoriasis: { ...BASE_DESCRIPTOR },
    isInfections: { ...BASE_DESCRIPTOR },
    isAcne: { ...BASE_DESCRIPTOR },
    isAllergicReaction: { ...BASE_DESCRIPTOR }
  },
  [ANALYSIS_RESULT_COLORS.BLUE]: {
    dysfunctionScore: 0,
    observedDryness: { ...BASE_DESCRIPTOR },
    observedDehydration: { ...BASE_DESCRIPTOR },
    predictiveFactorsDryness: { ...BASE_DESCRIPTOR },
    predictiveFactorsDehydration: { ...BASE_DESCRIPTOR }
  },
  [ANALYSIS_RESULT_COLORS.ORANGE]: {
    dysfunctionScore: 0,
    oiliness: { ...BASE_DESCRIPTOR },
    pores: { ...BASE_DESCRIPTOR }
  },
  [ANALYSIS_RESULT_COLORS.GREY]: {
    dysfunctionScore: 0,
    roughBumpySkin: { ...BASE_DESCRIPTOR },
    dullSkin: { ...BASE_DESCRIPTOR },
    unevenSkinTexture: { ...BASE_DESCRIPTOR },
    roughness: { ...BASE_DESCRIPTOR },
    scarring: { ...BASE_DESCRIPTOR }
  },
  [ANALYSIS_RESULT_COLORS.GREEN]: {
    dysfunctionScore: 0,
    comedones: { ...BASE_DESCRIPTOR },
    pustules: { ...BASE_DESCRIPTOR },
    papules: { ...BASE_DESCRIPTOR },
    nodules: { ...BASE_DESCRIPTOR },
    cysts: { ...BASE_DESCRIPTOR }
  },
  [ANALYSIS_RESULT_COLORS.BROWN]: {
    dysfunctionScore: 0,
    melasma: { ...BASE_DESCRIPTOR },
    postInflammatoryHyperpigmentation: { ...BASE_DESCRIPTOR },
    ageSunSpots: { ...BASE_DESCRIPTOR },
    freckles: { ...BASE_DESCRIPTOR },
    moles: { ...BASE_DESCRIPTOR },
    skinTone: { ...BASE_DESCRIPTOR },
    predictiveFactorsHyperpigmentation: { ...BASE_DESCRIPTOR }
  }
};

function getAgeCategory(age) {
  if (age < 20) {
    return 'young';
  }
  if (age >= 20 && age < 30) {
    return 'youngAdult';
  }
  if (age >= 30 && age < 50) {
    return 'adult';
  }
  if (age >= 50) {
    return 'senior';
  }
}

function getAgeCategoryValue(age) {
  if (age.replace(/\s+/g, '').toLowerCase() === 'Young'.toLowerCase()) {
    return 'young';
  }
  if (age.replace(/\s+/g, '').toLowerCase() === 'Young Adult'.replace(/\s+/g, '').toLowerCase()) {
    return 'youngAdult';
  }
  if (age.replace(/\s+/g, '').toLowerCase() === 'Adult'.toLowerCase()) {
    return 'adult';
  }
  if (age.replace(/\s+/g, '').toLowerCase() === 'Older Adult'.replace(/\s+/g, '').toLowerCase()) {
    return 'senior';
  }
  return 'senior';
}

function isString(variable) {
  return typeof variable === 'string';
}

function extractIntegerIndex(str) {
  const match = str.match(/\d+$/); // This will match one or more digits at the end of the string
  return match ? parseInt(match[0], 10) : null; // Returns the integer or null if no number is found
}

function replaceInnerObjectWithValue(obj) {
  // eslint-disable-next-line no-restricted-syntax, no-unused-vars
  for (const key in obj) {
    if (obj !== null && obj.hasOwnProperty(key)) {
      const value = obj[key];

      // If the property is an object and has a 'value' field, replace the object with the 'value'
      if (typeof value === 'object' && value !== null && value.hasOwnProperty('value')) {
        obj[key] = value.value;
      } else if (typeof value === 'object' && value !== null) {
        // If it's an object but doesn't have 'value', recursively process it
        replaceInnerObjectWithValue(value);
      }
    }
  }
  return obj;
}

export default {
  name: 'PhotoAnalysisResultUpdate',
  components: {
    DashboardConstraint,
    QuitPhotoAnalysisWarningModal,
    ZoomablePhoto,
    ModalHeaderWithPrevious,
    QSelect,
    VSelect,
    MobileFixedFooter,
    SlideDown,
    DiagnosticDysfunctionSlider
  },
  mixins: [isAlmaTerritories],
  data() {
    return {
      // scrollbar: null,
      ageSelectOptions: [],
      personalDataFields: [],
      selectFieldsBinding: [],
      sectionsBinding: [],
      selectOptions: SELECT_OPTIONS_FIELDS,
      selectFieldsData: {
        ...CHARS_CONFIG,
        ...SECTIONS_DYSFUNCTIONS_FIELDS_V2
      },
      sectionsScores: {
        ...SECTIONS_SCORE_DEFAULT_VALUES
      },
      ageGroupScrollbar: null,
      scoreSectionScrollbar: null,
      photoUrl: null,
      isOpened: false,
      skinAnalysisConstraint: false
    };
  },
  computed: {
    ...mapGetters({
      currentPatientId: types.getters.CURRENT_PATIENT_ID,
      photoAnalysisPhotoId: types.getters.PHOTO_ANALYSIS_PHOTO_ID,
      initialUpdatedResults: types.getters.PHOTO_ANALYSIS_UPDATED_RESULTS
    }),
    hasSkinAnalysisConstraintError() {
      return this.errors.has('skinAnalysisConstraint');
    },

    personalizedSectionsBinding() {
      return this.sectionsBinding.map(section => {
        const enhancedQuestions = section.questions.map(question => {
          const sectionData = this.selectFieldsData?.[section.propertyName]?.[
            question.propertyName
          ];

          if (sectionData?.description) {
            const descriptionExists = (question.options || []).some(
              opt => opt.label === sectionData.description
            );

            if (!descriptionExists) {
              question.options = [
                {
                  label: sectionData.description,
                  value: `${question.propertyName}_description`,
                  multichoices: sectionData.multichoices,
                  isPersonalized: true
                },
                ...(question.options || [])
              ];
            }
          }

          return question;
        });

        return {
          ...section,
          questions: enhancedQuestions
        };
      });
    }
  },
  async mounted() {
    if (!isMobileDevice()) {
      const ageGroupRef = this.$refs['age-group-container'].querySelector(
        '.multiselect__content-wrapper'
      );

      const scoreSectionRef = this.$refs['scores-section'];

      const scrollbarOptions = {
        suppressScrollX: true,
        wheelPropagation: false
      };

      this.ageGroupScrollbar = new PerfectScrollbar(ageGroupRef, scrollbarOptions);
      this.scoreSectionScrollbar = new PerfectScrollbar(scoreSectionRef, scrollbarOptions);
    }

    this.createSelectOptions();
    this.selectFieldsBinding = this.getSelectFieldsBinding();
    this.sectionsBinding = this.getSectionsBinding();
    this.setInitialResultData();
    this.personalDataFields = this.getPersonalDataFields();

    const { data: photo } = await fetchPatientPhotoByPhotoId(
      this.currentPatientId,
      this.photoAnalysisPhotoId
    );

    this.photoUrl = buildImageUrl(photo);
  },
  methods: {
    ...mapActions({
      updatePhotoAnalysisResults: types.actions.SET_PHOTO_ANALYSIS_UPDATED_RESULTS
    }),
    updatePatientEmailConsent(emailConsent) {
      this.$emit('update-email-consent', emailConsent);
    },
    getNestedProperty(obj, path) {
      // alert(path);
      return path.split('.').reduce((acc, key) => (acc ? acc[key] : undefined), obj);
    },
    beforeCloseModal() {
      this.$modal.show('quit-photo-analysis-warning-modal', {
        noAnswerHandler: () => {
          this.$modal.show('photo-analysis-result-update-modal');
        }
      });

      this.$modal.hide('photo-analysis-result-update-modal');
    },
    setInitialResultData() {
      const { ageGroup } = this.initialUpdatedResults.characteristics;
      // eslint-disable-next-line no-alert
      // alert(ageGroup);
      const ageGroupValue = Number.isInteger(ageGroup)
        ? ageGroup
        : this.$t(
          `dashboard.photoAnalysis.label.${this.initialUpdatedResults.characteristics.ageGroup}`
        );

      // alert(ageGroupValue);
      set(this.selectFieldsData, 'characteristics.ageGroup', ageGroupValue);

      PERSON_DATA_FIELDS_NAMES.forEach(fieldName => {
        const initialValue = get(this.initialUpdatedResults, ['characteristics', fieldName], null);
        const optionsPropertyName = FIELD_TO_OPTIONS_PROPERTY_BINDING[fieldName];
        set(
          this.selectFieldsData,
          'characteristics'.concat('.'.concat(fieldName)),
          this.selectOptions[optionsPropertyName].find(
            ({ value }) => String(value) === String(initialValue)
          )
        );
      });

      PERSON_DATA_FIELDS_DEFAULT_VALUES.forEach(fieldName => {
        const initialValue = get(this.initialUpdatedResults, ['characteristics', fieldName], null);
        // eslint-disable-next-line no-alert
        // alert(`${fieldName} ${initialValue}`);

        set(
          this.selectFieldsData,
          'characteristics'.concat('.'.concat(fieldName)),
          String(initialValue)
        );
      });

      // pour chaque culeur des culeurs
      // ANALYSIS_RESULT_COLORS
      ANALYSIS_RESULT_COLORS_ARRAY.forEach(color => {
        SELECT_DATA_FIELDS_NAMES.forEach(fieldName => {
          const initialValue = get(
            this.initialUpdatedResults,
            [color, fieldName, 'description'],
            null
          );
          if (initialValue != null) {
            set(
              this.selectFieldsData,
              color.concat('.'.concat(fieldName).concat('.description')),
              initialValue
            );
            const multichoix = get(
              this.initialUpdatedResults,
              [color, fieldName, 'multichoices'],
              null
            );
            set(
              this.selectFieldsData,
              color.concat('.'.concat(fieldName).concat('.multichoices')),
              multichoix
            );
          }
        });
      });

      SECTIONS_SCORES_FIELDS.forEach(scoreField => {
        this.sectionsScores[scoreField] = get(
          this.initialUpdatedResults,
          [scoreField, 'dysfunctionScore'],
          null
        );
        set(
          this.selectFieldsData,
          scoreField.concat('.dysfunctionScore'),
          get(this.initialUpdatedResults, [scoreField, 'dysfunctionScore'], null)
        );
      });
    },
    getPersonalDataFields() {
      return PERSON_DATA_FIELDS_NAMES.map(field => ({
        ...this.selectFieldsBinding[field],
        tooltip: this.$t(get(TOOLTIP_CONFIG, [field, 'content']))
      }));
    },
    createSelectOptions() {
      this.ageSelectOptions = AGE_GROUPS.map(({ ageGroup, ages }) => ({
        ageGroup: this.$t(`dashboard.photoAnalysis.label.${ageGroup}`),
        ages
      }));

      PHOTO_ANALYSIS_SELECT_OPTIONS_BINDING.forEach(({ propertyName, options, keyPrefix }) => {
        this.selectOptions[propertyName] = getSelectOptionsWithLabel({
          $t: this.$t,
          options,
          keyPrefix
        });
      });

      MEDICAL_DATA_SELECT_OPTIONS_BINDING.forEach(binding => {
        this.selectOptions[binding.propertyName] = getMedicalDataSelectOptionsWithLabel(
          this.$t,
          binding.options
        );
      });
    },
    getSelectFieldsBinding() {
      const {
        genderSelectOptions,
        phototypeSelectOptions,
        skinThinknessSelectOptions,
        yesNoSelectOptions,
        noModerateSevereSelectOptions,
        noMildModerateSevereSelectOptions,
        yComplexionSelectOptions,
        yTirednessSelectOptions,
        ySunDamageOptions,
        pWrinklesOptions,
        pFineLineseOptions,
        pElasticitySaggingOptions,
        pVolumeeOptions,
        rRednessPresentOptions,
        rCouperosePresentOptions,
        rIsRosaceaOptions,
        rIsSunburnOptions,
        rIsContactDermatitisOptions,
        rIsEczemaOptions,
        rIsPsoriasisOptions,
        rIsInfectionsOptions,
        rIsAcneOptions,
        rIsAllergicReactionOptions,
        bObservedDrynessOptions,
        bObservedDehydrationOptions,
        bPredictiveFactorsDrynessOptions,
        bPredictiveFactorsDehydrationOptions,
        oOilinessOptions,
        oPoresOptions,
        greyRoughBumpySkinOptions,
        greyDullSkinOptions,
        greyUnevenSkinTextureOptions,
        greyRoughnessOptions,
        greyScarringOptions,
        greenComedonesOptions,
        greenPustulesOptions,
        greenPapulesOptions,
        greenNodulesOptions,
        greenCystsOptions,
        brownMelasmaOptions,
        brownPostInflammatoryHyperpigmentationOptions,
        brownAgeSunSpotsOptions,
        brownFrecklesOptions,
        brownMolesOptions,
        brownSkinToneOptions,
        brownPredictiveFactorsHyperpigmentationOptions
      } = this.selectOptions;

      return {
        gender: new SelectDataBinding(
          this.$t('dashboard.patientCard.gender'),
          'gender',
          genderSelectOptions
        ),
        phototype: new SelectDataBinding(
          this.$t('dashboard.medicalData.phototype'),
          'phototype',
          phototypeSelectOptions
        ),
        skinThickness: new SelectDataBinding(
          this.$t('dashboard.medicalData.skinThickness'),
          'skinThickness',
          skinThinknessSelectOptions
        ),
        hasAcneScars: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.acneScars'),
          'hasAcneScars',
          yesNoSelectOptions
        ),
        wrinklesDepth: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.wrinkles'),
          'wrinklesDepth',
          noModerateSevereSelectOptions
        ),
        hasRosacea1: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.rosacea'),
          'hasRosacea1',
          yesNoSelectOptions
        ),
        hasCouperose: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.couperose'),
          'hasCouperose',
          yesNoSelectOptions
        ),
        dryness: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.dryness'),
          'dryness',
          noMildModerateSevereSelectOptions
        ),
        dehydration: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.dehydration'),
          'dehydration',
          noMildModerateSevereSelectOptions
        ),
        hasAcneVulgaris: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.acneVulgaris'),
          'hasAcneVulgaris',
          yesNoSelectOptions
        ),
        hasAcneRosacea: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.acneRosacea'),
          'hasAcneRosacea',
          yesNoSelectOptions
        ),
        hasAcneNodular: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.acneNodular'),
          'hasAcneNodular',
          yesNoSelectOptions
        ),
        comesFromPIH: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.pih'),
          'comesFromPIH',
          yesNoSelectOptions
        ),
        comesFromAgeSpotsMelasma: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.ageSpot'),
          'comesFromAgeSpotsMelasma',
          yesNoSelectOptions
        ),
        sensitiveSkin: new SelectDataBinding(
          this.$t('dashboard.dysfunction.sensitiveSkin.title'),
          'sensitiveSkin',
          yesNoSelectOptions
        ),
        complexion: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.complexion'),
          'complexion',
          yComplexionSelectOptions
        ),
        tiredness: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.tiredness'),
          'tiredness',
          yTirednessSelectOptions
        ),
        sunDamage: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.sunDamage'),
          'sunDamage',
          ySunDamageOptions
        ),
        wrinkles: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.wrinkles'),
          'wrinkles',
          pWrinklesOptions
        ),
        fineLines: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.fineLines'),
          'fineLines',
          pFineLineseOptions
        ),
        elasticitySagging: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.elasticitySagging'),
          'elasticitySagging',
          pElasticitySaggingOptions
        ),
        volume: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.volume'),
          'volume',
          pVolumeeOptions
        ),
        rednessPresent: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.rednessPresent'),
          'rednessPresent',
          rRednessPresentOptions
        ),
        couperosePresent: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.couperosePresent'),
          'couperosePresent',
          rCouperosePresentOptions
        ),
        isRosacea: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.isRosacea'),
          'isRosacea',
          rIsRosaceaOptions
        ),
        isSunburn: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.isSunburn'),
          'isSunburn',
          rIsSunburnOptions
        ),
        isContactDermatitis: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.isContactDermatitis'),
          'isContactDermatitis',
          rIsContactDermatitisOptions
        ),
        isEczema: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.isEczema'),
          'isEczema',
          rIsEczemaOptions
        ),
        isPsoriasis: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.isPsoriasis'),
          'isPsoriasis',
          rIsPsoriasisOptions
        ),
        isInfections: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.isInfections'),
          'isInfections',
          rIsInfectionsOptions
        ),
        isAcne: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.isAcne'),
          'isAcne',
          rIsAcneOptions
        ),
        isAllergicReaction: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.isAllergicReaction'),
          'isAllergicReaction',
          rIsAllergicReactionOptions
        ),
        observedDryness: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.observedDryness'),
          'observedDryness',
          bObservedDrynessOptions
        ),
        observedDehydration: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.observedDehydration'),
          'observedDehydration',
          bObservedDehydrationOptions
        ),
        predictiveFactorsDryness: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.predictiveFactorsDryness'),
          'predictiveFactorsDryness',
          bPredictiveFactorsDrynessOptions
        ),
        predictiveFactorsDehydration: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.predictiveFactorsDehydration'),
          'predictiveFactorsDehydration',
          bPredictiveFactorsDehydrationOptions
        ),
        oiliness: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.oiliness'),
          'oiliness',
          oOilinessOptions
        ),
        pores: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.pores'),
          'pores',
          oPoresOptions
        ),
        roughBumpySkin: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.roughBumpySkin'),
          'roughBumpySkin',
          greyRoughBumpySkinOptions
        ),
        dullSkin: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.dullSkin'),
          'dullSkin',
          greyDullSkinOptions
        ),
        unevenSkinTexture: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.unevenSkinTexture'),
          'unevenSkinTexture',
          greyUnevenSkinTextureOptions
        ),
        roughness: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.roughness'),
          'roughness',
          greyRoughnessOptions
        ),
        scarring: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.scarring'),
          'scarring',
          greyScarringOptions
        ),
        comedones: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.comedones'),
          'comedones',
          greenComedonesOptions
        ),
        pustules: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.pustules'),
          'pustules',
          greenPustulesOptions
        ),
        papules: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.papules'),
          'papules',
          greenPapulesOptions
        ),
        nodules: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.nodules'),
          'nodules',
          greenNodulesOptions
        ),
        cysts: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.cysts'),
          'cysts',
          greenCystsOptions
        ),
        melasma: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.melasma'),
          'melasma',
          brownMelasmaOptions
        ),
        postInflammatoryHyperpigmentation: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.postInflammatoryHyperpigmentation'),
          'postInflammatoryHyperpigmentation',
          brownPostInflammatoryHyperpigmentationOptions
        ),
        ageSunSpots: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.ageSunSpots'),
          'ageSunSpots',
          brownAgeSunSpotsOptions
        ),
        freckles: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.freckles'),
          'freckles',
          brownFrecklesOptions
        ),
        moles: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.moles'),
          'moles',
          brownMolesOptions
        ),
        skinTone: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.skinTone'),
          'skinTone',
          brownSkinToneOptions
        ),
        predictiveFactorsHyperpigmentation: new SelectDataBinding(
          this.$t('dashboard.photoAnalysis.label.predictiveFactorsHyperpigmentation'),
          'predictiveFactorsHyperpigmentation',
          brownPredictiveFactorsHyperpigmentationOptions
        )
      };
    },
    getSectionsBinding() {
      const {
        complexion,
        tiredness,
        sunDamage,
        wrinkles,
        fineLines,
        elasticitySagging,
        volume,
        rednessPresent,
        couperosePresent,
        isRosacea,
        isSunburn,
        isContactDermatitis,
        isEczema,
        isPsoriasis,
        isInfections,
        isAcne,
        isAllergicReaction,
        observedDryness,
        observedDehydration,
        predictiveFactorsDryness,
        predictiveFactorsDehydration,
        oiliness,
        pores,
        roughBumpySkin,
        dullSkin,
        unevenSkinTexture,
        roughness,
        scarring,
        comedones,
        pustules,
        papules,
        nodules,
        cysts,
        melasma,
        postInflammatoryHyperpigmentation,
        ageSunSpots,
        freckles,
        moles,
        skinTone,
        predictiveFactorsHyperpigmentation
      } = this.selectFieldsBinding;

      return [
        new PhotoAnalysisResultSection({
          name: this.$t(`dashboard.diagnostic.${ACTIVES.OXIDATIVE_STRESS}`),
          propertyName: 'yellow',
          color: DYSFUNCTIONS_COLORS[ACTIVES.OXIDATIVE_STRESS],
          questions: [complexion, tiredness, sunDamage]
        }),
        new PhotoAnalysisResultSection({
          name: this.$t(`dashboard.diagnostic.${ACTIVES.SKIN_APPEARANCE}`),
          propertyName: 'pink',
          color: DYSFUNCTIONS_COLORS[ACTIVES.SKIN_APPEARANCE],
          questions: [wrinkles, fineLines, elasticitySagging, volume]
        }),
        new PhotoAnalysisResultSection({
          name: this.$t(`dashboard.diagnostic.${ACTIVES.SKIN_REDNESS}`),
          propertyName: 'red',
          color: DYSFUNCTIONS_COLORS[ACTIVES.SKIN_REDNESS],
          questions: [
            rednessPresent,
            couperosePresent,
            isRosacea,
            isSunburn,
            isContactDermatitis,
            isEczema,
            isPsoriasis,
            isInfections,
            isAcne,
            isAllergicReaction
          ]
        }),
        new PhotoAnalysisResultSection({
          name: this.$t(`dashboard.diagnostic.${ACTIVES.SKIN_DRYNESS}`),
          propertyName: 'blue',
          color: DYSFUNCTIONS_COLORS[ACTIVES.SKIN_DRYNESS],
          questions: [
            observedDryness,
            observedDehydration,
            predictiveFactorsDryness,
            predictiveFactorsDehydration
          ]
        }),
        new PhotoAnalysisResultSection({
          name: this.$t(`dashboard.diagnostic.${ACTIVES.SKIN_OILINESS}`),
          propertyName: 'orange',
          color: DYSFUNCTIONS_COLORS[ACTIVES.SKIN_OILINESS],
          questions: [oiliness, pores]
        }),
        new PhotoAnalysisResultSection({
          name: this.$t(`dashboard.diagnostic.${ACTIVES.SKIN_TEXTURE}`),
          propertyName: 'grey',
          color: DYSFUNCTIONS_COLORS[ACTIVES.SKIN_TEXTURE],
          questions: [roughBumpySkin, dullSkin, unevenSkinTexture, roughness, scarring]
        }),
        new PhotoAnalysisResultSection({
          name: this.$t(`dashboard.diagnostic.${ACTIVES.PIMPLES}`),
          propertyName: 'green',
          color: DYSFUNCTIONS_COLORS[ACTIVES.PIMPLES],
          questions: [comedones, pustules, papules, nodules, cysts]
        }),
        new PhotoAnalysisResultSection({
          name: this.$t(`dashboard.diagnostic.${ACTIVES.SKIN_PIGMENTATION}`),
          propertyName: 'brown',
          color: DYSFUNCTIONS_COLORS[ACTIVES.SKIN_PIGMENTATION],
          questions: [
            melasma,
            postInflammatoryHyperpigmentation,
            ageSunSpots,
            freckles,
            moles,
            skinTone,
            predictiveFactorsHyperpigmentation
          ]
        })
      ];
    },
    onFieldChange(sectionName, propertyName, option) {
      const sectionTXT = sectionName.concat('.'.concat(propertyName));
      const sectionDesc = sectionTXT.concat('.description');
      const sectionMultichoice = sectionTXT.concat('.multichoices');
      const multichoiceValue = get(option, 'value', '0');
      // eslint-disable-next-line no-alert
      // alert(JSON.stringify(option));

      if (option.isPersonalized) {
        set(this.selectFieldsData, sectionMultichoice, option.multichoices);
        set(this.selectFieldsData, sectionDesc, get(option, 'label', ''));
      } else {
        set(this.selectFieldsData, sectionMultichoice, extractIntegerIndex(multichoiceValue));
        set(this.selectFieldsData, sectionDesc, get(option, 'label', ''));
      }
    },
    onAgeGroupFieldChange(propertyName, option) {
      // alert(`estimated age${option}`);

      set(this.selectFieldsData, 'characteristics'.concat('.').concat('ageGroup'), Number(option));
      set(
        this.selectFieldsData,
        'characteristics'.concat('.').concat('estimatedAge'),
        Number(option)
      );
    },
    onCaractheristicFieldChange(propertyName, option) {
      set(this.selectFieldsData, 'characteristics'.concat('.').concat(propertyName), option);
    },
    updateScore(propertyName, value) {
      this.sectionsScores[propertyName] = value;
      const toto = propertyName.concat('.'.concat('dysfunctionScore'));

      set(this.selectFieldsData, toto, value);
    },
    async onNextClick() {
      // eslint-disable-next-line no-console
      // console.log(JSON.stringify(this.selectFieldsData));
      // eslint-disable-next-line no-console
      // console.log(JSON.stringify(replaceInnerObjectWithValue(this.selectFieldsData)));
      // eslint-disable-next-line no-alert
      if (!(await this.$validator.validateAll())) {
        this.$scrollTo('#dashboard-consent', { duration: 100, offset: -80 });
        return;
      }

      const ageGroup = get(
        this.selectFieldsData,
        'characteristics'.concat('.').concat('ageGroup'),
        null
      );
      // eslint-disable-next-line no-alert
      // alert(ageGroup);
      if (isNumber(ageGroup)) {
        set(
          this.selectFieldsData,
          'characteristics'.concat('.').concat('ageGroup'),
          getAgeCategory(ageGroup)
        );
      }
      if (isString(ageGroup)) {
        set(
          this.selectFieldsData,
          'characteristics'.concat('.').concat('ageGroup'),
          getAgeCategoryValue(ageGroup)
        );
      }
      await this.updatePhotoAnalysisResults(replaceInnerObjectWithValue(this.selectFieldsData));

      handleRoutingInfo(CLARIFICATION_ROUNTING_INFO, this.$router, this.$modal);
      this.closeModal();
    },
    closeModal() {
      this.$modal.hide('photo-analysis-result-update-modal');
    }
  }
};
</script>

<style lang="scss" scoped>
@import '~perfect-scrollbar/css/perfect-scrollbar.css';
@import '../../../../../../assets/css/common/perfect-scrollbar-styles';
@import '../../../../../../assets/scss/common/buttons';
@import '../../../../../../assets/scss/helpers/colors';
@import '../../../../../../assets/css/common/icons.css';
@import '../../../../../../assets/css/common/tooltips.css';

@import '../../../../../../assets/scss/helpers/colors';
@import '../../../../../../assets/scss/treatment/create-treatment-shared';

.result {
  &__content {
    height: 580px;
    display: flex;
    background-color: $white;
    color: $text-color;
  }

  &__questions {
    margin-top: 20px;
    width: 330px;
    padding-right: 20px;
    align-items: flex-start;
  }

  &__scores {
    width: 480px;
    position: relative;
    padding-right: 20px;
    padding-top: 20px;
    padding-left: 20px;
  }

  &__section {
    width: 100%;

    display: flex;
    justify-content: center;

    padding-top: 20px;
    padding-bottom: 20px;
    font-family: Inter;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    letter-spacing: -0.006em;
    text-align: left;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;

    &:not(:last-child) {
      border-bottom: 1px solid $dim-white;
    }
  }

  &__footer {
    height: 70px;

    background-color: $white;
    border-top: 1px solid $dim-white;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }

  .header {
    &__step {
      color: $grey-color;
      font-weight: bold;
    }
  }

  .section {
    &__container {
      width: 410px;

      display: flex;
      flex-direction: column;
      align-items: flex-end;

      padding: 0 60px 0 5px;
    }

    &__section-name {
      font-size: 15px;
      line-height: 17px;

      margin-right: 15px;

      color: $text-color;
      text-align: right;
    }

    &__score {
      display: flex;
      align-items: center;
    }
  }

  .field-container {
    /*width: 100%;*/
    display: flex;
    align-items: center;

    &__title {
      font-size: 13px;
      text-align: right;
      font-weight: 300;
      width: 130px;
      padding-left: 20px;
    }

    .select-container {
      margin-left: 2px;
    }

    &.personal-data-select {
      .select-container {
        padding-right: 2px;
        width: 250px;
      }
    }
  }

  .zoomable-photo-desktop {
    margin: 15px 0 0 20px;
  }

  .question {
    &__label {
      text-align: left;
      font-weight: 300;
      font-family: Inter;
      font-size: 14px;
      font-weight: 500;
      line-height: 20px;
      letter-spacing: -0.006em;
      text-underline-position: from-font;
      text-decoration-skip-ink: none;
      padding-bottom: 10px;
    }

    &__field {
      position: relative;
      &:first-child {
        margin-top: 15px;
      }

      .select-container {
        --vs-border-radius: 0.7em;
        width: 430px;
        margin-left: 0px;
      }
    }
  }

  @media (min-width: 768px) {
    .zoomable-photo-mobile {
      display: none;
    }

    .result {
      &__section {
        &:nth-child(3) {
          padding-top: 6px;
        }
      }
    }
  }

  @media (max-width: 767px) {
    .result {
      min-height: 100vh;

      &__modal-header {
        position: fixed;
        top: 0;

        z-index: 51;

        background-color: $background;
      }

      &__content {
        height: auto;

        flex-direction: column;
        flex: 1;

        padding-top: 70px;
      }

      &__questions {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;

        padding-right: 0;
      }

      &__scores {
        width: 100%;

        display: flex;
        flex-direction: column;
        align-items: center;
      }

      &__footer {
        height: 60px;

        z-index: 50;

        border-top: 0;
        box-shadow: 0px -6px 7px rgba(148, 166, 176, 0.15);
      }
      &__section {
        //padding-left: 6px;

        //padding-right: 6px;
        overflow: hidden;
      }
    }

    .header {
      &__title,
      &__step {
        color: $main-title-color;
        font-weight: initial;
      }
    }

    .section {
      &__container {
        width: 100%;
      }

      &__score {
        justify-content: flex-end;

        margin-bottom: 0;
      }

      &__section-name {
        font-size: 13px;
        line-height: 15px;
      }

      &__questions {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
      }
    }

    .field-container {
      width: 100%;

      &__title {
        font-weight: normal;
        width: 100%;
        text-align: left;
        padding-left: 0px;
      }

      .select-container {
        width: 100%;
        max-width: 430px;
      }
    }

    .zoomable-photo-desktop {
      display: none;
    }

    .zoomable-photo-mobile {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      margin-top: 20px;
      align-self: center;
      order: 999;
    }

    .question {
      &__label {
        font-size: 12px;
        width: 100%;
      }

      &__field {
        width: 100%;
      }
    }
  }
}

.skin-analysis {
  border-top: 1px solid #eaeef0;
  padding: 10px 20px 0;
  &__constraint {
    margin-bottom: 10px;
  }
}

@media (max-width: 767px) {
  .skin-analysis {
    margin-bottom: 60px;
  }
}

::v-deep .vs__dropdown-menu {
  .vs__dropdown-option {
    border: 1px solid #e8e8e8;
    border-radius: 4px;
    margin: 4px 8px;
    padding: 8px 12px;
  }
}
</style>
